import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { submitTransaction } from "@/lib/offline";
import { CYLINDERS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Stepper } from "@/components/Stepper";
import { toast } from "sonner";
import { Camera, AlertTriangle } from "lucide-react";

function resizeImage(file, maxDim = 900) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > height && width > maxDim) { height = (height * maxDim) / width; width = maxDim; }
        else if (height > maxDim) { width = (width * maxDim) / height; height = maxDim; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function NonConsumerSale() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [cyl, setCyl] = useState("COM_19");
  const [qty, setQty] = useState(1);
  const [empties, setEmpties] = useState(0);
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("cash");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);

  const onPhoto = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const data = await resizeImage(f);
    setPhoto(data);
  };

  const submit = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error("Enter the amount collected"); return; }
    if (mode === "upi" && !photo) { toast.error("UPI photo proof is required"); return; }
    setBusy(true);
    try {
      const res = await submitTransaction({
        type: "nonconsumer_sale", customer_name: name, customer_phone: phone,
        cylinder_type: cyl, qty, amount: parseFloat(amount), payment_mode: mode,
        upi_photo: photo, empties_collected: empties,
      });
      toast.success(res.queued ? "Saved on phone — will sync when online" : "Non-consumer sale recorded");
      navigate("/driver");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 fade-up">
      <h1 className="font-head text-xl font-bold">Non-consumer sale</h1>
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 flex gap-2">
        <AlertTriangle className="h-4 w-4 mt-0.5" /> Cash sale with no consumer number. Record the full amount — the owner reconciles this later.
      </div>
      <Card className="p-4 space-y-3">
        <div><Label>Customer name (optional)</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="h-12" data-testid="sale-name-input" /></div>
        <div><Label>Phone (optional)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))} className="h-12 font-mono" /></div>
      </Card>
      <Card className="p-4 space-y-3">
        <Label>Cylinder type</Label>
        <div className="grid grid-cols-2 gap-2">
          {CYLINDERS.map((c) => (
            <button key={c.code} onClick={() => setCyl(c.code)} data-testid={`sale-cyl-${c.code}`}
              className={`rounded-xl border p-3 text-sm font-medium ${cyl === c.code ? "border-primary bg-primary/10 text-primary" : "bg-card"}`}>{c.label}</button>
          ))}
        </div>
        <div className="flex items-center justify-between pt-2"><Label>Quantity</Label><Stepper value={qty} onChange={(v) => setQty(Math.max(1, v))} min={1} testid="sale-qty" /></div>
        <div className="flex items-center justify-between"><div><Label>Empties collected</Label><p className="text-xs text-muted-foreground">Usually 0 — bump up only if an empty came back</p></div><Stepper value={empties} onChange={(v) => setEmpties(Math.max(0, v))} min={0} testid="sale-empties" /></div>
      </Card>
      <Card className="p-4 space-y-3">
        <div><Label>Amount collected (₹)</Label><Input type="number" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 text-base" placeholder="Enter amount" data-testid="driver-nonconsumer-amount-input" /></div>
        <Label>Payment</Label>
        <div className="grid grid-cols-2 gap-2">
          {["cash", "upi"].map((m) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`sale-mode-${m}`}
              className={`rounded-xl border p-3 font-medium uppercase ${mode === m ? "border-primary bg-primary/10 text-primary" : "bg-card"}`}>{m}</button>
          ))}
        </div>
        {mode === "upi" && (
          <div className="space-y-2">
            <Label>UPI transaction photo (required)</Label>
            <input type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" id="upi-photo" data-testid="driver-nonconsumer-upi-photo-input" />
            <label htmlFor="upi-photo" className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed h-24 cursor-pointer text-muted-foreground">
              {photo ? <img src={photo} alt="proof" className="h-20 rounded-lg" /> : <><Camera className="h-5 w-5" /> Tap to capture</>}
            </label>
          </div>
        )}
      </Card>
      <div className="fixed bottom-0 inset-x-0 max-w-md mx-auto p-4 bg-background/95 backdrop-blur border-t">
        <Button className="w-full h-14 text-base" onClick={submit} disabled={busy} data-testid="driver-nonconsumer-submit-button">Record sale</Button>
      </div>
    </div>
  );
}
